import { Box, IconButton, MenuItem } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';

import CheckBox from '@components/check-box';
import BlackTooltip from '@components/common/BlackTooltip';
import { CaretDownIcon } from '@components/icons';
import Colors from 'styles/colors';

const MultiSelect = ({
  options,
  className,
  state,
  setState,
  getOptionView,
  getSelectedOptionsView,
  content,
  selectAllValue,
  showTooltip,
  singleSelectMode,
  width,
  iconView,
  anchorOrigin,
  transformOrigin
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const onOptionClick = (targetValue, isSingleClick) => {
    const isSelectAll = selectAllValue === targetValue;
    if (isSelectAll) {
      if (state.length === options.length) {
        return setState(state);
      }
      return setState(options.map(option => option.value));
    }

    if (isSingleClick) {
      return setState([targetValue]);
    }

    if (state.includes(targetValue)) {
      const filteredState = state.filter(item => ![targetValue, selectAllValue].includes(item));
      if (!filteredState.length) return null;
      return setState(filteredState);
    }
    return setState([...state, targetValue]);
  };
  const onMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {iconView ? (
        <IconButton className="p-2" onClick={event => setAnchorEl(event.currentTarget)}>
          {iconView}
        </IconButton>
      ) : (
        <Button
          aria-owns={anchorEl ? 'simple-menu' : undefined}
          aria-haspopup="true"
          className={classNames(
            'flex items-center justify-start cursor-pointer select-none px-2 py-1',
            className
          )}
          onClick={event => setAnchorEl(event.currentTarget)}
          style={{ textTransform: 'none' }}
        >
          <div className="flex items-center justify-between">
            {content || getSelectedOptionsView(state)}
            <CaretDownIcon
              fill={Colors.gray.dark}
              width="16px"
              height="14px"
              className="ml-2 hidden xs:block"
            />
          </div>
        </Button>
      )}
      <Menu
        MenuListProps={{
          style: { width, maxHeight: '75vh' },
          className: 'p-2 scroll-container'
        }}
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        {options.map(optionData => {
          const isChecked = state.includes(optionData.value);
          const onClick = () => onOptionClick(optionData.value);
          const isSelectAll = selectAllValue === optionData.value;
          const { subValues } = optionData;

          const optionView = getOptionView(optionData);

          if (subValues) {
            return (
              <MultiSelect
                options={subValues}
                className="w-full"
                state={state}
                setState={setState}
                getOptionView={getOptionView}
                content={<div className="text-base font-normal">{optionView}</div>}
                selectAllValue={selectAllValue}
                showTooltip={showTooltip}
                singleSelectMode={singleSelectMode}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                width={optionData.width || width}
              />
            );
          }
          const getWrapperMenuItem = () => {
            const menuItem = (
              <MenuItem
                variant="text"
                className={classNames(
                  'h-10 flex flex-grow items-center justify-start rounded pl-2',
                  { 'mr-2': !subValues }
                )}
                onClick={() => {
                  if (optionData.disabled || subValues) return;
                  onOptionClick(optionData.value, true);
                  onMenuClose();
                }}
              >
                {getOptionView(optionData)}
              </MenuItem>
            );
            if (!isSelectAll && showTooltip && !subValues) {
              return (
                <BlackTooltip title="Only" placement="left" arrow>
                  {menuItem}
                </BlackTooltip>
              );
            }
            return menuItem;
          };
          return (
            <Box className="flex items-center justify-between" key={optionData.value}>
              {getWrapperMenuItem()}
              {!isSelectAll && !singleSelectMode && !subValues && (
                <CheckBox
                  value={isChecked}
                  classNameContainer="flex-shrink-0 h-6 mr-2"
                  classNameCheckBox="h-5 w-5"
                  onCheckHandler={onClick}
                  disabled={optionData.disabled}
                />
              )}
            </Box>
          );
        })}
      </Menu>
    </>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.instanceOf(Array).isRequired,
  className: PropTypes.string,
  state: PropTypes.instanceOf(Array).isRequired,
  setState: PropTypes.func.isRequired,
  getOptionView: PropTypes.func.isRequired,
  getSelectedOptionsView: PropTypes.func.isRequired,
  selectAllValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTooltip: PropTypes.bool,
  singleSelectMode: PropTypes.bool,
  width: PropTypes.number,
  iconView: PropTypes.node,
  content: PropTypes.node,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.string,
    horizontal: PropTypes.string
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.string,
    horizontal: PropTypes.string
  })
};

MultiSelect.defaultProps = {
  className: '',
  selectAllValue: null,
  showTooltip: false,
  singleSelectMode: false,
  width: 280,
  iconView: null,
  content: null,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'start'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'start'
  }
};

export default MultiSelect;
